import type { FC } from 'react';
import React, { useRef } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { Category } from '@graphql/generated-base';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import type { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useScaleGuestSiteFontSize } from '@shared/core/website/theming/useScaleGuestSiteFontSize';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
import { useFitText } from '../hooks/useFitText';

// Renders the event display name in the same serif event-title font as the hero title.
const typographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE },
];

// Horizontal margin so the name doesn't touch the edges while still using the full available
// width (matches the hero title's input-area margins).
const NAME_MARGIN_X_MOBILE = 24;
const NAME_MARGIN_X_DESKTOP = 64;

// Upper bound for the fitted name size; useFitText scales down from here to fit the available width.
const NAME_MAX_FONT_SIZE_PX = 55;

// useFitText fits by width AND height, reading the container's height up-front. The hero passes a
// fixed-height container; ours hugs its content (auto height), so a height constraint would lock the
// font to the initial small line height. A negative-infinity heightOffset makes the allowed height
// (clientHeight - heightOffset) unbounded, so the name is sized purely by the available width.
const DISABLE_HEIGHT_CONSTRAINT = Number.NEGATIVE_INFINITY;

type MonogramEventDisplayNameProps = Readonly<{
  content: string;
  /** Custom color; falls back to the page font color when absent. */
  color?: string;
}>;

/**
 * Event display name shown directly below the monogram on the photo/video
 * background monogram layouts. Mirrors the hero title's responsive treatment:
 * the event-title serif font, a responsive variant, and `useFitText` so the
 * name scales to the full available width (minus side margins) instead of being
 * capped at the monogram width.
 */
export const MonogramEventDisplayName: FC<MonogramEventDisplayNameProps> = ({ content, color }) => {
  const { pageFontColor } = useColorPaletteProvider();
  const textRef = useRef<HTMLDivElement>(null);

  const [isXsScreen = false] = useResponsive({ values: { _: true, xs: false } });
  const [marginX = NAME_MARGIN_X_MOBILE] = useResponsive({ values: { _: NAME_MARGIN_X_MOBILE, md: NAME_MARGIN_X_DESKTOP } });

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const {
    containerRef,
    fontSize: fitFontSize,
    isReady,
  } = useFitText({
    textRef,
    maxFontSize: Number.parseFloat(scaleFontSize(pxToRem(NAME_MAX_FONT_SIZE_PX), Category.EVENT_TITLE)) * 16,
    widthOffset: marginX * 2,
    heightOffset: DISABLE_HEIGHT_CONSTRAINT,
    minFontSize: 10,
  });

  return (
    <GuestSiteTypographyOverride override={typographyOverride}>
      <Flex
        ref={containerRef}
        width="100%"
        justifyContent="center"
        paddingX={marginX}
      >
        <TextV2
          ref={textRef}
          typographyVariant={isXsScreen ? 'display4' : 'display6'}
          color={color ?? pageFontColor}
          whiteSpace="nowrap"
          textAlign="center"
          transition="opacity 150ms ease-in"
          // Hide until useFitText computes the fitted size to avoid a flash from maxFontSize.
          style={{ fontSize: fitFontSize, opacity: isReady ? 1 : 0 }}
        >
          {content}
        </TextV2>
      </Flex>
    </GuestSiteTypographyOverride>
  );
};

MonogramEventDisplayName.displayName = 'MonogramEventDisplayName';
